import { initialValues, ToolEquipmentChangeReasonFormValues } from './schema';
import { getToolEquipmentChangeReasonColumns } from './cols-definition';
import { DeleteDialog, ErpDataTableV2 } from '@/components/custom/erp';
import type { LyDoTangGiamCCDC } from '@/types/schemas';
import { ActionBar, FormDialog } from './components';
import { useLyDoTangGiamCCDC } from './hooks';
import { useForms } from '@/hooks';

export default function LyDoTangGiamCCDC() {
  const {
    toolEquipmentChangeReasons,
    isLoading,
    addToolEquipmentChangeReason,
    updateToolEquipmentChangeReason,
    deleteToolEquipmentChangeReason,
    refreshToolEquipmentChangeReasons
  } = useLyDoTangGiamCCDC();

  const {
    showForm,
    showDelete: showDeleteDialog,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<LyDoTangGiamCCDC>();

  // Map API data to form values with proper initial values for selects
  const mapApiToFormData = (apiData: LyDoTangGiamCCDC): ToolEquipmentChangeReasonFormValues => {
    return {
      loai_tg_cc: apiData.loai_tg_cc || '1', // Default to '1' (Tăng công cụ)
      ma_tg_cc: apiData.ma_tg_cc || '',
      ten_tg_cc: apiData.ten_tg_cc || '',
      ten_tg_cc2: apiData.ten_tg_cc2 || '',
      status: apiData.status || '1', // Default to '1' (Còn sử dụng)
      action: apiData.action || 'CUSTOM',
      param: apiData.param || 'MANUAL'
    };
  };

  const mappedToolEquipmentChangeReasons = toolEquipmentChangeReasons.map(reason => ({
    ...reason,
    id: reason.uuid
  }));

  const handleCodeClick = (uuid: string) => {
    const row = toolEquipmentChangeReasons.find(r => r.uuid === uuid);
    if (row) {
      const mappedRow = { ...row, id: row.uuid };
      clearSelection();
      setTimeout(() => {
        handleRowClick({ row: mappedRow, id: mappedRow.id });
        handleViewClick();
      }, 0);
    }
  };

  const handleSubmitForm = async (data: ToolEquipmentChangeReasonFormValues) => {
    try {
      if (formMode === 'add') {
        await addToolEquipmentChangeReason(data);
      } else if (formMode === 'edit' && selectedObj) {
        await updateToolEquipmentChangeReason(selectedObj.uuid, data);
      }

      await refreshToolEquipmentChangeReasons();
      handleCloseForm();
      clearSelection();
    } catch (error) {
      console.error('Error submitting form:', error);
      throw error;
    }
  };

  const handleDeleteConfirm = async (uuid: string) => {
    try {
      await deleteToolEquipmentChangeReason(uuid);
      // 🔥 CRITICAL: Refresh data sau khi xóa
      await refreshToolEquipmentChangeReasons();
      clearSelection();
    } catch (error) {
      console.error('Error deleting reason:', error);
      throw error;
    }
  };

  const handleRefreshClick = async () => {
    try {
      await refreshToolEquipmentChangeReasons();
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  const handleExportClick = () => {
    console.log('Export data clicked');
    // TODO: Implement export functionality
  };

  const handleFixedColumnsClick = () => {
    console.log('Fixed columns clicked');
    // TODO: Implement fixed columns functionality
  };

  const tables = [
    {
      name: '',
      rows: mappedToolEquipmentChangeReasons,
      columns: getToolEquipmentChangeReasonColumns(handleCodeClick)
    }
  ];

  // Calculate initial data based on form mode
  const getInitialData = (): ToolEquipmentChangeReasonFormValues => {
    if (formMode === 'edit' || formMode === 'view') {
      return selectedObj ? mapApiToFormData(selectedObj) : initialValues;
    }

    if (formMode === 'add' && isCopyMode && selectedObj) {
      return {
        ...mapApiToFormData(selectedObj),
        ma_tg_cc: '', // Clear main identifier for copy
        ten_tg_cc: `${selectedObj.ten_tg_cc} ` // Add space for copy mode
      };
    }

    // Default case for add mode without copy - use initialValues from schema
    return initialValues;
  };

  return (
    <div className='flex h-full min-h-screen w-full min-w-0 max-w-full flex-col lg:overflow-hidden'>
      {showForm && (
        <FormDialog
          open={showForm}
          mode={formMode}
          initialData={getInitialData()}
          onClose={handleCloseForm}
          selectedObj={selectedObj}
          onSubmit={handleSubmitForm}
          onAddButtonClick={handleAddClick}
          onEditButtonClick={handleEditClick}
          onDeleteButtonClick={handleDeleteClick}
          onCopyButtonClick={handleCopyClick}
        />
      )}

      {showDeleteDialog && selectedObj && (
        <DeleteDialog
          open={showDeleteDialog}
          onClose={handleCloseDelete}
          deleteObj={handleDeleteConfirm}
          clearSelection={clearSelection}
          selectedObj={selectedObj}
        />
      )}

      <div className='w-full overflow-hidden'>
        <ActionBar
          selectedObj={selectedObj}
          onAddIconClick={handleAddClick}
          onEditIconClick={handleEditClick}
          onDeleteIconClick={handleDeleteClick}
          onCopyIconClick={handleCopyClick}
          onWatchIconClick={handleViewClick}
          onRefreshClick={handleRefreshClick}
          onExportClick={handleExportClick}
          onFixedColumnsClick={handleFixedColumnsClick}
        />

        <ErpDataTableV2
          tables={tables}
          selectedRowId={selectedRowIndex !== null ? String(selectedRowIndex) : undefined}
          onRowClick={handleRowClick}
          loading={isLoading}
        />
      </div>
    </div>
  );
}
